import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Table,
  Tag,
  message,
  Avatar,
  Descriptions,
  Timeline,
  Empty,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Tooltip,
  Space,
  Popconfirm,
} from 'antd';
import {
  EditOutlined,
  ArrowRightOutlined,
  BankOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SwapOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { bankAccountsApi } from '../../utils/api';
import { DetailPageSkeleton } from '../../components/SkeletonLoaders';
import { formatMoneyFa, formatPriceToman } from '../../utils/formatPriceToman';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';

const { Option } = Select;
const { TextArea } = Input;

const toPersianNumber = (num) => {
  if (num === null || num === undefined) return '-';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

const jalaliMonthByGregorianLabel = {
  january: 'دی',
  jan: 'دی',
  ژانویه: 'دی',
  فوریه: 'بهمن',
  february: 'بهمن',
  feb: 'بهمن',
  مارس: 'اسفند',
  march: 'اسفند',
  mar: 'اسفند',
  آوریل: 'فروردین',
  آپریل: 'فروردین',
  april: 'فروردین',
  apr: 'فروردین',
  مه: 'اردیبهشت',
  may: 'اردیبهشت',
  ژوئن: 'خرداد',
  june: 'خرداد',
  jun: 'خرداد',
  جولای: 'تیر',
  july: 'تیر',
  jul: 'تیر',
  آگوست: 'مرداد',
  اوت: 'مرداد',
  august: 'مرداد',
  aug: 'مرداد',
  سپتامبر: 'شهریور',
  september: 'شهریور',
  sep: 'شهریور',
  اکتبر: 'مهر',
  october: 'مهر',
  oct: 'مهر',
  نوامبر: 'آبان',
  november: 'آبان',
  nov: 'آبان',
  دسامبر: 'آذر',
  december: 'آذر',
  dec: 'آذر',
};

const formatChartMonthLabel = (label) => {
  const key = String(label || '').trim().toLowerCase();
  return jalaliMonthByGregorianLabel[key] || label || '-';
};


const ViewBankAccount = () => {
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const { id } = useParams();
  const { canEdit, canDelete } = useResourcePermissions('bankaccount');
  const [loading, setLoading] = useState(true);
  const [bankAccount, setBankAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [chartData, setChartData] = useState(null);

  const [depositModalVisible, setDepositModalVisible] = useState(false);
  const [withdrawModalVisible, setWithdrawModalVisible] = useState(false);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [viewTransactionModalVisible, setViewTransactionModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [otherAccounts, setOtherAccounts] = useState([]);

  const [depositButtonLoading, setDepositButtonLoading] = useState(false);
  const [withdrawButtonLoading, setWithdrawButtonLoading] = useState(false);
  const [transferButtonLoading, setTransferButtonLoading] = useState(false);
  const [viewTransactionLoading, setViewTransactionLoading] = useState(null);
  const [deleteTransactionLoading, setDeleteTransactionLoading] = useState(null);

  const [depositForm] = Form.useForm();
  const [withdrawForm] = Form.useForm();
  const [transferForm] = Form.useForm();

  const goBackToList = () => {
    navigate(returnToRef.current || '/finance/bank-accounts', { state: buildGoBackState() });
  };

  useEffect(() => {
    fetchBankAccount();
  }, [id]);

  const fetchBankAccount = async () => {
    setLoading(true);
    try {
      const res = await bankAccountsApi.getById(id);
      if (res?.data) {
        setBankAccount(res.data.bank_account);
        setTransactions(Array.isArray(res.data.bank_transactions) ? res.data.bank_transactions : []);
        setRecentTransactions(res.data.recentTransactions || []);
        if (res.data.creditVsDebitChart) {
          setChartData(res.data.creditVsDebitChart);
        }
      }
    } catch (error) {
      console.error('Error fetching bank account:', error);
      message.error('خطا در دریافت اطلاعات حساب بانکی');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionCreateData = async (type) => {
    try {
      const res = await bankAccountsApi.getTransactionCreateData(type, id);
      if (res?.data?.bank_accounts) {
        setOtherAccounts(res.data.bank_accounts);
      }
    } catch (error) {
      console.error('Error fetching transaction create data:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return toPersianNumber(date.toLocaleDateString('fa-IR'));
  };

  const getTransactionTitle = (title) => {
    const titles = {
      'bank-account-created': 'ایجاد حساب',
      'bank-account-updated': 'بروزرسانی حساب',
      'bank-account-deposit': 'واریز',
      'bank-account-withdraw': 'برداشت',
      'bank-account-transfer': 'انتقال',
      'expense-added': 'ثبت هزینه',
      'expense-updated': 'بروزرسانی هزینه',
      'expense-deleted': 'حذف هزینه',
      'invoice-payment-received': 'دریافت پرداخت فاکتور',
      'invoice-payment-deleted': 'حذف پرداخت فاکتور',
    };
    return titles[title] || title;
  };

  const getTotalCredit = () => {
    if (!chartData?.values?.[0]) return 0;
    return chartData.values[0].reduce((sum, val) => sum + val, 0);
  };

  const getTotalDebit = () => {
    if (!chartData?.values?.[1]) return 0;
    return chartData.values[1].reduce((sum, val) => sum + val, 0);
  };

  const openDepositModal = async () => {
    setDepositButtonLoading(true);
    try {
      await fetchTransactionCreateData('deposit');
      depositForm.resetFields();
      setDepositModalVisible(true);
    } finally {
      setDepositButtonLoading(false);
    }
  };

  const openWithdrawModal = async () => {
    setWithdrawButtonLoading(true);
    try {
      await fetchTransactionCreateData('withdraw');
      withdrawForm.resetFields();
      setWithdrawModalVisible(true);
    } finally {
      setWithdrawButtonLoading(false);
    }
  };

  const openTransferModal = async () => {
    setTransferButtonLoading(true);
    try {
      await fetchTransactionCreateData('account');
      transferForm.resetFields();
      setTransferModalVisible(true);
    } finally {
      setTransferButtonLoading(false);
    }
  };

  const handleDeposit = async (values) => {
    setTransactionLoading(true);
    try {
      await bankAccountsApi.createTransaction({
        type: 'deposit',
        to_bank_account: id,
        amount: values.amount,
        memo: values.memo || '',
      });
      message.success('واریز با موفقیت انجام شد');
      setDepositModalVisible(false);
      fetchBankAccount();
    } catch (error) {
      message.error('خطا در انجام واریز');
    } finally {
      setTransactionLoading(false);
    }
  };

  const handleWithdraw = async (values) => {
    setTransactionLoading(true);
    try {
      await bankAccountsApi.createTransaction({
        type: 'withdraw',
        from_bank_account: id,
        amount: values.amount,
        memo: values.memo || '',
      });
      message.success('برداشت با موفقیت انجام شد');
      setWithdrawModalVisible(false);
      fetchBankAccount();
    } catch (error) {
      message.error('خطا در انجام برداشت');
    } finally {
      setTransactionLoading(false);
    }
  };

  const handleTransfer = async (values) => {
    setTransactionLoading(true);
    try {
      await bankAccountsApi.createTransaction({
        type: 'account',
        from_bank_account: id,
        to_bank_account: values.to_bank_account,
        amount: values.amount,
        exchange_rate: values.exchange_rate || 1,
        memo: values.memo || '',
      });
      message.success('انتقال با موفقیت انجام شد');
      setTransferModalVisible(false);
      fetchBankAccount();
    } catch (error) {
      message.error('خطا در انجام انتقال');
    } finally {
      setTransactionLoading(false);
    }
  };

  const viewTransaction = async (transactionId) => {
    setViewTransactionLoading(transactionId);
    try {
      const res = await bankAccountsApi.getTransaction(transactionId);
      if (res?.data?.bank_transaction) {
        setSelectedTransaction(res.data.bank_transaction);
        setViewTransactionModalVisible(true);
      }
    } catch (error) {
      message.error('خطا در دریافت اطلاعات تراکنش');
    } finally {
      setViewTransactionLoading(null);
    }
  };

  const deleteTransaction = async (transactionId) => {
    setDeleteTransactionLoading(transactionId);
    try {
      await bankAccountsApi.deleteTransaction(transactionId);
      message.success('تراکنش با موفقیت حذف شد');
      fetchBankAccount();
    } catch (error) {
      message.error('خطا در حذف تراکنش');
    } finally {
      setDeleteTransactionLoading(null);
    }
  };

  const accountTypes = {
    saving: 'پس‌انداز',
    current: 'جاری',
    'credit-card': 'کارت اعتباری',
    loan: 'وام',
    overdraft: 'اضافه برداشت',
  };

  const currencySymbol = bankAccount?.currency?.currency_symbol || 'تومان';

  const transactionColumns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>تاریخ</span>,
      dataIndex: 'transaction_date',
      key: 'transaction_date',
      render: (date) => <span style={{ fontFamily: 'CustomFont' }}>{formatDate(date)}</span>,
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>عنوان</span>,
      dataIndex: 'title',
      key: 'title',
      render: (title) => <span style={{ fontFamily: 'CustomFont' }}>{getTransactionTitle(title)}</span>,
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>مبلغ</span>,
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => (
        <span className={record.type === 'Cr' ? 'text-green-600' : 'text-red-600'} style={{ fontFamily: 'CustomFont' }}>
          {record.type === 'Cr' ? '+' : '-'}{formatMoneyFa(amount, record.currency_symbol)}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>نوع</span>,
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'Cr' ? 'green' : 'red'} style={{ fontFamily: 'CustomFont' }}>
          {type === 'Cr' ? 'بستانکار' : 'بدهکار'}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>موجودی</span>,
      dataIndex: 'bank_balance',
      key: 'bank_balance',
      render: (balance, record) => (
        <span style={{ fontFamily: 'CustomFont' }}>{formatMoneyFa(balance, record.currency_symbol)}</span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>عملیات</span>,
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="مشاهده">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => viewTransaction(record.id)}
              loading={viewTransactionLoading === record.id}
            />
          </Tooltip>
          {canDelete ? (
            <Popconfirm
              title="آیا از حذف این تراکنش مطمئن هستید؟"
              onConfirm={() => deleteTransaction(record.id)}
              okText="بله"
              cancelText="خیر"
              okButtonProps={{ className: 'bg-red-600 hover:bg-red-700 border-0 text-white' }}
            >
              <Tooltip title="حذف">
                <Button type="text" danger icon={<DeleteOutlined />} loading={deleteTransactionLoading === record.id} />
              </Tooltip>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  if (loading || !bankAccount) {
    return (
      <MainLayout>
        <div style={{ fontFamily: 'CustomFont' }}>
          <DetailPageSkeleton />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          {/* هدر */}
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <BankOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">جزئیات حساب بانکی</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">{bankAccount.account_name}{bankAccount.bank_name ? ` - ${bankAccount.bank_name}` : ''}</p>
              </div>
            </div>
            <Space wrap>
              <Button icon={<ArrowRightOutlined />} onClick={goBackToList} style={{ fontFamily: 'CustomFont' }}>
                بازگشت
              </Button>
              {canEdit ? (
                <Button icon={<EditOutlined />} onClick={() => navigate(`/finance/bank-accounts/${id}/edit`)} style={{ fontFamily: 'CustomFont' }}>
                  ویرایش
                </Button>
              ) : null}
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {/* اطلاعات حساب و موجودی */}
            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-6">
              <div className="flex items-center gap-4 mb-6">
                {bankAccount.type === 'bank' && bankAccount.bank_logo ? (
                  <Avatar src={bankAccount.file_url} size={64} icon={<BankOutlined />} className="bg-blue-100" />
                ) : (
                  <Avatar
                    size={64}
                    icon={bankAccount.type === 'bank' ? <BankOutlined /> : <DollarOutlined />}
                    className={bankAccount.type === 'bank' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'}
                  />
                )}
                <div>
                  <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 m-0" style={{ fontFamily: 'CustomFont' }}>
                    {bankAccount.account_name}
                  </h2>
                  {bankAccount.bank_name && (
                    <p className="text-gray-500 dark:text-gray-400 text-sm m-0" style={{ fontFamily: 'CustomFont' }}>{bankAccount.bank_name}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <Tag color={bankAccount.type === 'bank' ? 'blue' : 'green'} style={{ fontFamily: 'CustomFont' }}>
                      {bankAccount.type === 'bank' ? 'حساب بانکی' : 'صندوق نقدی'}
                    </Tag>
                    <Tag color={bankAccount.status === 1 ? 'green' : 'red'} style={{ fontFamily: 'CustomFont' }}>
                      {bankAccount.status === 1 ? 'فعال' : 'غیرفعال'}
                    </Tag>
                  </div>
                </div>
              </div>
            </Card>

            {/* آمار مالی */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
  <div>
    <div className="text-gray-500 text-sm mb-2" style={{ fontFamily: 'CustomFont' }}>موجودی فعلی</div>
    <div
      style={{
        fontFamily: 'CustomFont',
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: bankAccount.bank_balance >= 0 ? '#16a34a' : '#dc2626',
      }}
    >
      <WalletOutlined style={{ marginLeft: 8 }} />
      {formatPriceToman(bankAccount.bank_balance, { showCurrency: true })}
    </div>
  </div>
</Card>
<Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
  <div>
    <div className="text-gray-500 text-sm mb-2" style={{ fontFamily: 'CustomFont' }}>موجودی اولیه</div>
    <div
      style={{
        fontFamily: 'CustomFont',
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: '#1f2937',
      }}
    >
      <DollarOutlined style={{ marginLeft: 8 }} />
      {formatPriceToman(bankAccount.opening_balance, { showCurrency: true })}
    </div>
  </div>
</Card>
              <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Button
                    type="primary"
                    icon={<ArrowDownOutlined />}
                    onClick={openDepositModal}
                    loading={depositButtonLoading}
                    className="bg-green-600 hover:bg-green-700 border-0 flex-1"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    واریز
                  </Button>
                  <Button
                    type="primary"
                    icon={<ArrowUpOutlined />}
                    onClick={openWithdrawModal}
                    loading={withdrawButtonLoading}
                    className="bg-red-600 hover:bg-red-700 border-0 flex-1"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    برداشت
                  </Button>
                  <Button
                    type="primary"
                    icon={<SwapOutlined />}
                    onClick={openTransferModal}
                    loading={transferButtonLoading}
                    className="bg-blue-600 hover:bg-blue-700 border-0 flex-1"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    انتقال
                  </Button>
                </div>
              </Card>
            </div>

            {/* اطلاعات حساب */}
            <Card
              title={<span style={{ fontFamily: 'CustomFont' }}>اطلاعات حساب</span>}
              className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-6"
            >
              <Descriptions
                bordered
                column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
                style={{ fontFamily: 'CustomFont' }}
              >
                <Descriptions.Item label="نام حساب">{bankAccount.account_name}</Descriptions.Item>
                <Descriptions.Item label="نوع حساب">
                  <Tag color={bankAccount.type === 'bank' ? 'blue' : 'green'} style={{ fontFamily: 'CustomFont' }}>
                    {bankAccount.type === 'bank' ? 'حساب بانکی' : 'صندوق نقدی'}
                  </Tag>
                </Descriptions.Item>
                {bankAccount.bank_name && (
                  <Descriptions.Item label="نام بانک">{bankAccount.bank_name}</Descriptions.Item>
                )}
                {bankAccount.account_type && (
                  <Descriptions.Item label="نوع">{accountTypes[bankAccount.account_type] || bankAccount.account_type}</Descriptions.Item>
                )}
                {bankAccount.account_number && (
                  <Descriptions.Item label="شماره حساب">{toPersianNumber(bankAccount.account_number)}</Descriptions.Item>
                )}
                {bankAccount.contact_number && (
                  <Descriptions.Item label="شماره تماس">{toPersianNumber(bankAccount.contact_number)}</Descriptions.Item>
                )}
                <Descriptions.Item label="وضعیت">
                  <Tag color={bankAccount.status === 1 ? 'green' : 'red'} style={{ fontFamily: 'CustomFont' }}>
                    {bankAccount.status === 1 ? 'فعال' : 'غیرفعال'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="واحد پولی">{bankAccount.currency?.currency_name || '-'} ({currencySymbol})</Descriptions.Item>
              </Descriptions>
            </Card>

            {/* خلاصه بستانکار و بدهکار */}
            {chartData?.labels && chartData.labels.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* مجموع بستانکار */}
<Card className="shadow-sm border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
  <div>
    <div className="text-green-700 dark:text-green-400 text-sm mb-2" style={{ fontFamily: 'CustomFont' }}>مجموع بستانکار</div>
    <div
      style={{
        fontFamily: 'CustomFont',
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: '#16a34a',
      }}
    >
      <ArrowDownOutlined style={{ marginLeft: 8 }} />
      {formatPriceToman(getTotalCredit(), { showCurrency: true })}
    </div>
  </div>
  <div className="mt-4 pt-3 border-t border-green-200 dark:border-green-800">
    {chartData.labels.map((label, index) => (
      <div key={index} className="flex justify-between items-center py-1.5 text-sm">
        <span className="text-gray-600 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>{formatChartMonthLabel(label)}</span>
        <span className="text-green-600 font-medium" style={{ fontFamily: 'CustomFont' }}>{formatMoneyFa(chartData.values[0][index], currencySymbol)}</span>
      </div>
    ))}
  </div>
</Card>

{/* مجموع بدهکار */}
<Card className="shadow-sm border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
  <div>
    <div className="text-red-700 dark:text-red-400 text-sm mb-2" style={{ fontFamily: 'CustomFont' }}>مجموع بدهکار</div>
    <div
      style={{
        fontFamily: 'CustomFont',
        fontSize: '1.5rem',
        fontWeight: 'bold',
        color: '#dc2626',
      }}
    >
      <ArrowUpOutlined style={{ marginLeft: 8 }} />
      {formatPriceToman(getTotalDebit(), { showCurrency: true })}
    </div>
  </div>
  <div className="mt-4 pt-3 border-t border-red-200 dark:border-red-800">
    {chartData.labels.map((label, index) => (
      <div key={index} className="flex justify-between items-center py-1.5 text-sm">
        <span className="text-gray-600 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>{formatChartMonthLabel(label)}</span>
        <span className="text-red-600 font-medium" style={{ fontFamily: 'CustomFont' }}>{formatMoneyFa(chartData.values[1][index], currencySymbol)}</span>
      </div>
    ))}
  </div>
</Card>
              </div>
            )}

            {/* تراکنش‌های اخیر */}
            {recentTransactions.length > 0 && (
              <Card
                title={<span style={{ fontFamily: 'CustomFont' }}>تراکنش‌های اخیر</span>}
                className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-6"
              >
                <Timeline
                  items={recentTransactions.slice(0, 5).map((tx) => ({
                    color: tx.title.includes('deposit') || tx.title.includes('created') ? 'green' : 'red',
                    children: (
                      <div style={{ fontFamily: 'CustomFont' }}>
                        <div className="font-medium text-gray-800 dark:text-gray-200">{getTransactionTitle(tx.title)}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {formatMoneyFa(tx.amount, currencySymbol)} - {formatDate(tx.transaction_date)}
                        </div>
                      </div>
                    ),
                  }))}
                />
              </Card>
            )}

            {/* جدول تراکنش‌ها */}
            <Card
              title={<span style={{ fontFamily: 'CustomFont' }}>تاریخچه تراکنش‌ها</span>}
              className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800"
            >
              {transactions.length > 0 ? (
                <Table
                  columns={transactionColumns}
                  dataSource={transactions}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => <span style={{ fontFamily: 'CustomFont' }}>مجموع: {toPersianNumber(total)} تراکنش</span>,
                  }}
                />
              ) : (
                <Empty description={<span style={{ fontFamily: 'CustomFont' }}>تراکنشی یافت نشد</span>} />
              )}
            </Card>
          </div>
        </Card>

        {/* Modal واریز */}
        <Modal
          title={<span style={{ fontFamily: 'CustomFont' }}>واریز به حساب</span>}
          open={depositModalVisible}
          onCancel={() => setDepositModalVisible(false)}
          footer={null}
          destroyOnClose
        >
          <Form form={depositForm} layout="vertical" onFinish={handleDeposit}>
            <Form.Item name="amount" label={<span style={{ fontFamily: 'CustomFont' }}>مبلغ</span>} rules={[{ required: true, message: 'لطفاً مبلغ را وارد کنید' }]}>
              <InputNumber
                className="w-full"
                placeholder="مبلغ واریز"
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/,/g, '')}
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>
            <Form.Item name="memo" label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}>
              <TextArea rows={3} placeholder="توضیحات (اختیاری)" style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setDepositModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>انصراف</Button>
              <Button type="primary" htmlType="submit" loading={transactionLoading} className="bg-green-600 hover:bg-green-700 border-0" style={{ fontFamily: 'CustomFont' }}>
                واریز
              </Button>
            </div>
          </Form>
        </Modal>

        {/* Modal برداشت */}
        <Modal
          title={<span style={{ fontFamily: 'CustomFont' }}>برداشت از حساب</span>}
          open={withdrawModalVisible}
          onCancel={() => setWithdrawModalVisible(false)}
          footer={null}
          destroyOnClose
        >
          <Form form={withdrawForm} layout="vertical" onFinish={handleWithdraw}>
            <Form.Item name="amount" label={<span style={{ fontFamily: 'CustomFont' }}>مبلغ</span>} rules={[{ required: true, message: 'لطفاً مبلغ را وارد کنید' }]}>
              <InputNumber
                className="w-full"
                placeholder="مبلغ برداشت"
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/,/g, '')}
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>
            <Form.Item name="memo" label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}>
              <TextArea rows={3} placeholder="توضیحات (اختیاری)" style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setWithdrawModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>انصراف</Button>
              <Button type="primary" htmlType="submit" loading={transactionLoading} className="bg-red-600 hover:bg-red-700 border-0" style={{ fontFamily: 'CustomFont' }}>
                برداشت
              </Button>
            </div>
          </Form>
        </Modal>

        {/* Modal انتقال */}
        <Modal
          title={<span style={{ fontFamily: 'CustomFont' }}>انتقال بین حساب‌ها</span>}
          open={transferModalVisible}
          onCancel={() => setTransferModalVisible(false)}
          footer={null}
          destroyOnClose
        >
          <Form form={transferForm} layout="vertical" onFinish={handleTransfer}>
            <Form.Item name="to_bank_account" label={<span style={{ fontFamily: 'CustomFont' }}>حساب مقصد</span>} rules={[{ required: true, message: 'لطفاً حساب مقصد را انتخاب کنید' }]}>
              <Select placeholder="انتخاب حساب مقصد" style={{ fontFamily: 'CustomFont' }}>
                {otherAccounts.map((acc) => (
                  <Option key={acc.id} value={acc.id}>
                    {acc.account_name} {acc.bank_name ? `(${acc.bank_name})` : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="amount" label={<span style={{ fontFamily: 'CustomFont' }}>مبلغ</span>} rules={[{ required: true, message: 'لطفاً مبلغ را وارد کنید' }]}>
              <InputNumber
                className="w-full"
                placeholder="مبلغ انتقال"
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/,/g, '')}
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>
            <Form.Item name="exchange_rate" label={<span style={{ fontFamily: 'CustomFont' }}>نرخ تبدیل</span>}>
              <InputNumber className="w-full" placeholder="نرخ تبدیل (پیش‌فرض: ۱)" defaultValue={1} style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>
            <Form.Item name="memo" label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}>
              <TextArea rows={3} placeholder="توضیحات (اختیاری)" style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setTransferModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>انصراف</Button>
              <Button type="primary" htmlType="submit" loading={transactionLoading} className="bg-blue-600 hover:bg-blue-700 border-0" style={{ fontFamily: 'CustomFont' }}>
                انتقال
              </Button>
            </div>
          </Form>
        </Modal>

        {/* Modal مشاهده تراکنش */}
        <Modal
          title={<span style={{ fontFamily: 'CustomFont' }}>جزئیات تراکنش</span>}
          open={viewTransactionModalVisible}
          onCancel={() => setViewTransactionModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setViewTransactionModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>بستن</Button>,
          ]}
        >
          {selectedTransaction && (
            <Descriptions column={1} bordered size="small" labelStyle={{ fontFamily: 'CustomFont' }} contentStyle={{ fontFamily: 'CustomFont' }}>
              <Descriptions.Item label="شناسه">{toPersianNumber(selectedTransaction.id)}</Descriptions.Item>
              <Descriptions.Item label="عنوان">{getTransactionTitle(selectedTransaction.title)}</Descriptions.Item>
              <Descriptions.Item label="مبلغ">
                <span className={selectedTransaction.type === 'Cr' ? 'text-green-600' : 'text-red-600'}>
                  {selectedTransaction.type === 'Cr' ? '+' : '-'}{formatMoneyFa(selectedTransaction.amount, selectedTransaction.bank_account?.currency?.currency_symbol || currencySymbol)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="نوع">
                <Tag color={selectedTransaction.type === 'Cr' ? 'green' : 'red'} style={{ fontFamily: 'CustomFont' }}>
                  {selectedTransaction.type === 'Cr' ? 'بستانکار' : 'بدهکار'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="موجودی پس از تراکنش">
                {formatMoneyFa(selectedTransaction.bank_balance, selectedTransaction.bank_account?.currency?.currency_symbol || currencySymbol)}
              </Descriptions.Item>
              <Descriptions.Item label="تاریخ">{formatDate(selectedTransaction.transaction_date)}</Descriptions.Item>
              {selectedTransaction.memo && (
                <Descriptions.Item label="توضیحات">{selectedTransaction.memo}</Descriptions.Item>
              )}
              <Descriptions.Item label="حساب">
                {selectedTransaction.bank_account?.account_name} {selectedTransaction.bank_account?.bank_name ? `(${selectedTransaction.bank_account.bank_name})` : ''}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default ViewBankAccount;
